import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Crown, Loader2, Shield, ShieldOff, RefreshCw } from 'lucide-react';

const ROLE_BADGE = {
  administrator: 'bg-[#EDE7F6] text-[#5A2F8E] border-[#9B7ED8]',
  supervisor: 'bg-[#E1F5FE] text-[#0277BD] border-[#81D4FA]',
  scheduler: 'bg-[#FFF8E1] text-[#9A6D00] border-[#FFD54F]',
};

export default function CeoLifeAccessManager({ api }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/ceo-life-access');
      setUsers(data || []);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Only administrators can manage CEO Life access');
      } else {
        toast.error('Failed to load access list');
      }
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const toggle = async (u) => {
    setBusyId(u.id);
    try {
      const { data } = await api.put(`/admin/ceo-life-access/${u.id}`, { grant: !u.has_access });
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, has_access: data.has_access } : x));
      toast.success(`${data.name} ${data.has_access ? 'granted' : 'revoked'} CEO Life access`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
    setBusyId(null);
  };

  if (loading) {
    return <Card className="border-[#DED8EB]"><CardContent className="p-12 text-center"><Loader2 className="h-7 w-7 animate-spin text-[#6B3FA0] mx-auto" /></CardContent></Card>;
  }

  const granted = users.filter(u => u.has_access);
  const revoked = users.filter(u => !u.has_access);

  return (
    <Card className="border-[#DED8EB]" data-testid="ceo-access-manager">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1a1325, #2D1B4E)' }}>
              <Crown className="h-5 w-5" style={{ color: '#FFD66B' }} />
            </div>
            <div>
              <CardTitle className="text-base font-bold text-[#2D1B4E]">CEO Life — workspace members</CardTitle>
              <p className="text-xs text-[#8E85A0] mt-0.5">
                Only granted users can read or write CEO Life data. Other admins see a greyed-out menu item but cannot enter.
              </p>
            </div>
          </div>
          <Button onClick={fetchUsers} variant="outline" size="sm" className="border-[#DED8EB] text-[#5A4D6E] gap-1.5" data-testid="ceo-access-refresh">
            <RefreshCw className="h-3.5 w-3.5" />Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3 text-xs">
          <span className="ceo-eyebrow flex items-center gap-1.5 text-[#5A4D6E]" style={{ letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600 }}>
            <Shield className="h-3 w-3 text-[#1B5E20]"/>{granted.length} member{granted.length === 1 ? '' : 's'}
          </span>
          <span className="text-[#8E85A0]">·</span>
          <span className="text-xs text-[#8E85A0]">{revoked.length} eligible (not yet granted)</span>
        </div>

        {/* Granted */}
        {granted.length > 0 && (
          <div className="border border-[#A5D6A7] bg-[#F1F8E9] rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-[#E8F5E9] border-b border-[#C5E1A5] text-[10px] uppercase tracking-wider font-bold text-[#1B5E20]">
              Has access
            </div>
            {granted.map(u => <UserRow key={u.id} u={u} onToggle={toggle} busy={busyId === u.id} granted />)}
          </div>
        )}

        {/* Revoked / eligible */}
        <div className="border border-[#DED8EB] rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-[#FAF8FE] border-b border-[#DED8EB] text-[10px] uppercase tracking-wider font-bold text-[#5A4D6E]">
            Eligible — not granted
          </div>
          {revoked.length === 0 ? (
            <p className="text-xs italic text-[#8E85A0] p-4 text-center">All eligible users already have access.</p>
          ) : revoked.map(u => <UserRow key={u.id} u={u} onToggle={toggle} busy={busyId === u.id} />)}
        </div>

        <p className="text-[11px] text-[#8E85A0] italic">
          Only administrators, supervisors, and schedulers can be granted CEO Life access. Staff-level accounts are not eligible.
        </p>
      </CardContent>
    </Card>
  );
}

function UserRow({ u, onToggle, busy, granted }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-[#F0EDF6] last:border-b-0 hover:bg-white/60 transition-colors" data-testid={`ceo-access-row-${u.id}`}>
      <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{ background: 'linear-gradient(135deg, #8367FF, #B49BFF)', color: '#0F0F18' }}>
        {(u.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-[#2D1B4E] truncate">{u.name || '(no name)'}</span>
          <Badge className={`text-[9px] border ${ROLE_BADGE[u.role] || ''}`}>{u.role}</Badge>
        </div>
        <p className="text-[11px] text-[#8E85A0] truncate">{u.email}</p>
      </div>
      <Button
        size="sm"
        onClick={() => onToggle(u)}
        disabled={busy}
        variant={granted ? 'outline' : 'default'}
        className={granted
          ? 'border-red-300 text-[#D9534F] hover:bg-red-50 gap-1.5 h-8'
          : 'bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1.5 h-8'}
        data-testid={`ceo-access-toggle-${u.id}`}
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : granted ? <ShieldOff className="h-3 w-3" /> : <Shield className="h-3 w-3" />}
        {granted ? 'Revoke' : 'Grant access'}
      </Button>
    </div>
  );
}
